import apiClient from "utils/apiClient";

const API_BASE = "/payment-service/api/v1";

const unwrap = (response) => response.data;
const pageContent = (result) => (Array.isArray(result) ? result : result?.content || []);

const withPaging = (params = {}) => {
  const { page = 0, size = 20, ...rest } = params;
  return { page, size, ...rest };
};

const withCorrelation = (correlationId) => {
  if (!correlationId) return {};
  return {
    headers: {
      "O-CORRELATION-ID": correlationId,
    },
  };
};

const isPersonalWorkspace = (workspace = {}) =>
  String(workspace?.accountType || "").toUpperCase() === "PERSONAL";

const subscriptionContextForWorkspace = (workspace = {}, actorId = "") => {
  if (isPersonalWorkspace(workspace)) {
    return {
      planType: "PERSONAL",
      subscriberType: "USER",
      subscriberId: actorId,
      organizationId: "",
      ready: Boolean(actorId),
    };
  }

  return {
    planType: "BUSINESS",
    subscriberType: "BUSINESS",
    subscriberId: workspace?.organizationId || "",
    organizationId: workspace?.organizationId || "",
    ready: Boolean(workspace?.organizationId),
  };
};

const checkoutChargeParams = (params = {}) => {
  const {
    stages = [],
    chargeTarget = "CHECKOUT",
    currency = "USD",
    country,
    page = 0,
    size = 50,
    ...rest
  } = params;
  const search = new URLSearchParams();
  Object.entries({
    chargeTarget,
    currency,
    country,
    page,
    size,
    ...rest,
  }).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, value);
    }
  });
  (Array.isArray(stages) ? stages : [stages]).filter(Boolean).forEach((stage) => {
    search.append("stages", stage);
  });
  return search;
};

export const identitySubscriptionService = {
  charges: {
    effectiveCheckout: async (params = {}) =>
      apiClient
        .get(`${API_BASE}/charges/effective/paged`, {
          params: checkoutChargeParams(params),
        })
        .then(unwrap),
  },

  coupons: {
    getByCode: async (code) =>
      apiClient.get(`${API_BASE}/payment-coupons/code/${encodeURIComponent(code)}`).then(unwrap),

    apply: async (data = {}) =>
      apiClient.post(`${API_BASE}/payment-coupons/apply`, data).then(unwrap),
  },

  plans: {
    list: async (params = {}) =>
      apiClient
        .get(`${API_BASE}/subscription-plans`, { params: withPaging(params) })
        .then(unwrap),

    get: async (planId, params = {}) =>
      apiClient
        .get(`${API_BASE}/subscription-plans/${planId}`, { params })
        .then(unwrap),

    create: async (data) =>
      apiClient.post(`${API_BASE}/subscription-plans`, data).then(unwrap),

    update: async (planId, data) =>
      apiClient.patch(`${API_BASE}/subscription-plans/${planId}`, data).then(unwrap),

    delete: async (planId) =>
      apiClient.delete(`${API_BASE}/subscription-plans/${planId}`).then(unwrap),
  },

  subscriptions: {
    list: async (params = {}) =>
      apiClient
        .get(`${API_BASE}/subscriptions`, { params: withPaging(params) })
        .then(unwrap),

    get: async (subscriptionId, params = {}) =>
      apiClient
        .get(`${API_BASE}/subscriptions/${subscriptionId}`, { params })
        .then(unwrap),

    listByPlan: async (planId, params = {}) =>
      apiClient
        .get(`${API_BASE}/subscriptions/plan/${planId}`, { params: withPaging(params) })
        .then(unwrap),

    create: async (data, params = {}, correlationId = "") =>
      apiClient.post(`${API_BASE}/subscriptions`, data, {
        params,
        ...withCorrelation(correlationId),
      }).then(unwrap),

    update: async (subscriptionId, data, params = {}, correlationId = "") =>
      apiClient.patch(`${API_BASE}/subscriptions/${subscriptionId}`, data, {
        params,
        ...withCorrelation(correlationId),
      }).then(unwrap),

    cancel: async (subscriptionId) =>
      apiClient.post(`${API_BASE}/subscriptions/${subscriptionId}/cancel`).then(unwrap),

    activeForWorkspace: async (workspace = {}, actorId = "") => {
      const context = subscriptionContextForWorkspace(workspace, actorId);
      const workspaceId = workspace?.id || workspace?.workspaceId || "";
      if (!workspaceId || !context.ready) return null;

      const result = await identitySubscriptionService.subscriptions.list({
        subscriberType: context.subscriberType,
        subscriberId: context.subscriberId,
        workspaceId,
        organizationId: context.organizationId || undefined,
        code: "CARE",
        status: "ACTIVE",
        page: 0,
        size: 100,
        sort: "startDate,desc",
        wid: workspaceId,
      });
      const subscriptions = pageContent(result);
      return (
        subscriptions.find((subscription) => String(subscription?.status || "").toUpperCase() === "ACTIVE") ||
        subscriptions[0] ||
        null
      );
    },
  },
};

export default identitySubscriptionService;
